import React, { useEffect, useState } from 'react';
import { Card, Table, Space, Empty, Button } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  CommunicationLogDto,
  fetchOrderCommunications,
} from '../modules/notifications/api/communicationsApi';
import CommunicationDetail, {
  CommunicationStatusTag,
} from '../modules/notifications/components/CommunicationDetail';
import { useFeedback } from '../utils/feedback';
import { getApiErrorMessage } from '../utils/apiError';

interface OrderCommunicationsProps {
  orderId: string;
}

const OrderCommunications: React.FC<OrderCommunicationsProps> = ({ orderId }) => {
  const { t } = useTranslation(['notifications', 'common']);
  const feedback = useFeedback();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<CommunicationLogDto[]>([]);
  const [selected, setSelected] = useState<CommunicationLogDto | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchOrderCommunications(orderId)
      .then((data) => { if (active) setLogs(data); })
      .catch((error) => { if (active) feedback.error(getApiErrorMessage(error, t, 'notifications:errors.loadFailed')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, t]);

  const columns = [
    {
      title: t('notifications:log_page.sentAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value: string) => new Date(value).toLocaleString(),
    },
    {
      title: t('notifications:log_page.eventType'),
      dataIndex: 'eventType',
      key: 'eventType',
      render: (value: string) => t(`notifications:eventTypes.${value}`, value),
    },
    {
      title: t('notifications:log_page.recipient'),
      key: 'recipient',
      render: (_: unknown, record: CommunicationLogDto) =>
        record.userName ? `${record.userName} (${record.recipient})` : record.recipient,
    },
    {
      title: t('notifications:log_page.channel'),
      dataIndex: 'channel',
      key: 'channel',
      render: (value: string) => t(`notifications:channels.${value}`, value),
    },
    {
      title: t('notifications:log_page.status'),
      dataIndex: 'status',
      key: 'status',
      render: (value: string) => <CommunicationStatusTag status={value} />,
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_: unknown, record: CommunicationLogDto) => (
        <Button type="link" size="small" onClick={() => setSelected(record)}>
          {t('notifications:comms.view')}
        </Button>
      ),
    },
  ];

  return (
    <Card
      title={
        <Space>
          <MailOutlined />
          {t('notifications:comms.orderSectionTitle')}
        </Space>
      }
      size="small"
    >
      {logs.length > 0 ? (
        <Table
          dataSource={logs}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="small"
          onRow={(record) => ({ onClick: () => setSelected(record) })}
        />
      ) : (
        <Empty
          description={t('notifications:log_page.noLogs')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}
      <CommunicationDetail
        record={selected}
        open={selected !== null}
        onClose={() => setSelected(null)}
      />
    </Card>
  );
};

export default OrderCommunications;
